import React, { useState, useEffect } from 'react';
import {
    Container, Paper, Typography, Box, TextField, Button, Alert,
    Grid, FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel,
    Divider, CircularProgress
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import api from '../services/api';
import { isAdmin } from '../utils/auth';
import { useNavigate, useParams } from 'react-router-dom';

function ConfiguracaoUsuario() {
    const navigate = useNavigate();
    const { id } = useParams(); // Para admin editar outros usuários
    const isAdminUser = isAdmin();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [usuario, setUsuario] = useState(null);
    const [formData, setFormData] = useState({
        tempo_inatividade: 30,
        hora_inicio: '07:00',
        hora_fim: '18:00',
        bloqueio_horario_ativo: true
    });

    useEffect(() => {
        carregarConfiguracoes();
    }, [id]);

    const carregarConfiguracoes = async () => {
        setLoading(true);
        setError('');
        try {
            if (isAdminUser && id) {
                // Admin editando outro usuário (tempo de inatividade apenas)
                const response = await api.get(`/configuracao-usuario/usuario/${id}`);
                setUsuario(response.data);
                setFormData({
                    tempo_inatividade: response.data.tempo_inatividade || 30,
                    hora_inicio: '07:00', // Não usado quando editando outro usuário
                    hora_fim: '18:00', // Não usado quando editando outro usuário
                    bloqueio_horario_ativo: true // Não usado quando editando outro usuário
                });
            } else if (isAdminUser && !id) {
                // Admin editando configurações globais + suas próprias
                const [responseGlobal, responseUsuario] = await Promise.all([
                    api.get('/configuracao-sistema'),
                    api.get('/configuracao-usuario/minhas-configuracoes')
                ]);
                setFormData({
                    tempo_inatividade: responseUsuario.data.tempo_inatividade || 30,
                    hora_inicio: responseGlobal.data.hora_inicio || '07:00',
                    hora_fim: responseGlobal.data.hora_fim || '18:00',
                    bloqueio_horario_ativo: responseGlobal.data.bloqueio_horario_ativo !== undefined 
                        ? responseGlobal.data.bloqueio_horario_ativo 
                        : true
                });
            } else {
                // Usuário comum editando suas próprias configurações (tempo de inatividade apenas)
                const [responseGlobal, responseUsuario] = await Promise.all([
                    api.get('/configuracao-sistema'),
                    api.get('/configuracao-usuario/minhas-configuracoes')
                ]);
                setFormData({
                    tempo_inatividade: responseUsuario.data.tempo_inatividade || 30,
                    hora_inicio: responseGlobal.data.hora_inicio || '07:00', // Apenas para exibição
                    hora_fim: responseGlobal.data.hora_fim || '18:00', // Apenas para exibição
                    bloqueio_horario_ativo: responseGlobal.data.bloqueio_horario_ativo !== undefined 
                        ? responseGlobal.data.bloqueio_horario_ativo 
                        : true // Apenas para exibição
                });
            }
        } catch (error) {
            console.error('Erro ao carregar configurações:', error);
            setError('Erro ao carregar configurações: ' + (error.response?.data?.error || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        setError('');
        setSuccess('');

        // Validações
        if (formData.tempo_inatividade < 1 || formData.tempo_inatividade > 1440) {
            setError('Tempo de inatividade deve ser entre 1 e 1440 minutos (24 horas)');
            setSaving(false);
            return;
        }

        if (!/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/.test(formData.hora_inicio)) {
            setError('Hora de início inválida. Use formato HH:MM (ex: 07:00)');
            setSaving(false);
            return;
        }

        if (!/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/.test(formData.hora_fim)) {
            setError('Hora de fim inválida. Use formato HH:MM (ex: 18:00)');
            setSaving(false);
            return;
        }

        try {
            if (isAdminUser && id) {
                // Admin atualizando outro usuário (apenas tempo de inatividade)
                await api.put(`/configuracao-usuario/usuario/${id}`, {
                    tempo_inatividade: formData.tempo_inatividade
                });
            } else if (isAdminUser && !id) {
                // Admin atualizando configurações globais + suas próprias
                await Promise.all([
                    api.put('/configuracao-sistema', {
                        hora_inicio: formData.hora_inicio,
                        hora_fim: formData.hora_fim,
                        bloqueio_horario_ativo: formData.bloqueio_horario_ativo
                    }),
                    api.put('/configuracao-usuario/minhas-configuracoes', {
                        tempo_inatividade: formData.tempo_inatividade
                    })
                ]);
            } else {
                // Usuário comum atualizando apenas tempo de inatividade
                await api.put('/configuracao-usuario/minhas-configuracoes', {
                    tempo_inatividade: formData.tempo_inatividade
                });
            }
            setSuccess('Configurações salvas com sucesso!');
            
            // Recarrega configurações para garantir sincronização
            setTimeout(() => {
                carregarConfiguracoes();
            }, 1000);
        } catch (error) {
            console.error('Erro ao salvar configurações:', error);
            setError('Erro ao salvar configurações: ' + (error.response?.data?.error || error.message));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Container maxWidth="md">
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="md">
            <Paper sx={{ p: 4, mt: 2 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    {isAdminUser && id 
                        ? `Configurações - ${usuario?.nome || 'Usuário'}` 
                        : isAdminUser && !id
                        ? 'Configurações do Sistema'
                        : 'Minhas Configurações'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {isAdminUser && !id
                        ? 'Configure o horário de uso global do sistema e seu tempo de inatividade pessoal'
                        : isAdminUser && id
                        ? 'Configure o tempo de inatividade para este usuário'
                        : 'Configure seu tempo de inatividade pessoal'}
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                        {success}
                    </Alert>
                )}

                <Divider sx={{ my: 3 }} />

                {/* Tempo de Inatividade */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" gutterBottom>
                        Bloqueio por Inatividade
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Defina o tempo (em minutos) de inatividade antes do sistema bloquear a sessão automaticamente.
                    </Typography>
                    <TextField
                        fullWidth
                        type="number"
                        label="Tempo de Inatividade (minutos)"
                        name="tempo_inatividade"
                        value={formData.tempo_inatividade}
                        onChange={handleChange}
                        inputProps={{ min: 1, max: 1440 }}
                        helperText="Entre 1 e 1440 minutos (24 horas). Padrão: 30 minutos"
                    />
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Horário de Uso */}
                {(!id || isAdminUser && !id) && (
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" gutterBottom>
                        {isAdminUser && !id ? 'Horário de Uso Global do Sistema' : 'Horário de Uso do Sistema'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {isAdminUser && !id
                            ? '⚠️ ATENÇÃO: Esta configuração afeta TODOS os usuários do sistema (exceto administradores). Fora deste horário, todos os usuários não-admin serão bloqueados.'
                            : 'O administrador define o horário permitido de uso. Entre em contato para alterações.'}
                    </Typography>

                    {isAdminUser && !id && (
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.bloqueio_horario_ativo}
                                    onChange={handleChange}
                                    name="bloqueio_horario_ativo"
                                />
                            }
                            label="Ativar bloqueio por horário global"
                            sx={{ mb: 2 }}
                        />
                    )}

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Hora de Início"
                                name="hora_inicio"
                                value={formData.hora_inicio}
                                onChange={handleChange}
                                type="time"
                                InputLabelProps={{ shrink: true }}
                                disabled={!isAdminUser || (isAdminUser && id)}
                                helperText={isAdminUser && !id 
                                    ? "Horário de início permitido para TODOS os usuários" 
                                    : "Definido pelo administrador"}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Hora de Fim"
                                name="hora_fim"
                                value={formData.hora_fim}
                                onChange={handleChange}
                                type="time"
                                InputLabelProps={{ shrink: true }}
                                disabled={!isAdminUser || (isAdminUser && id)}
                                helperText={isAdminUser && !id 
                                    ? "Horário de fim permitido para TODOS os usuários" 
                                    : "Definido pelo administrador"}
                            />
                        </Grid>
                    </Grid>

                    {isAdminUser && !id && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            ⚠️ Como administrador, você tem acesso ao sistema a qualquer hora, independente do horário configurado acima. Esta configuração afeta TODOS os usuários não-admin.
                        </Alert>
                    )}
                </Box>
                )}

                <Divider sx={{ my: 3 }} />

                {/* Botões */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                        variant="outlined"
                        onClick={() => navigate(-1)}
                        disabled={saving}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? 'Salvando...' : 'Salvar'}
                    </Button>
                </Box>
            </Paper>
        </Container>
    );
}

export default ConfiguracaoUsuario;
